import { useState, useEffect, useCallback } from "react";

type RegistryTool = {
  id: string;
  name: string;
  version: string;
  kind: string;
  summary: string;
  author: string;
  provides: string[];
  source: string;
  tags: string[];
  channel: string;
  has_readme: boolean;
  resource_count: number;
};

const CHANNELS = ["dev", "staging", "production"] as const;

const CHANNEL_COLORS: Record<string, string> = {
  dev: "#6272a4",
  staging: "#f1fa8c",
  production: "#50fa7b",
};

export function RegistryWorkbench() {
  const [tools, setTools] = useState<RegistryTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [promoting, setPromoting] = useState<string | null>(null);

  const fetchTools = useCallback(() => {
    setLoading(true);
    fetch("/api/registry/packages")
      .then((r) => r.json())
      .then((data) => {
        setTools(data.tools || []);
        setLoading(false);
      })
      .catch(() => {
        setTools([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const promote = useCallback(
    async (id: string, channel: string) => {
      setPromoting(id);
      try {
        const res = await fetch(`/api/registry/packages/${id}/promote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ channel }),
        });
        const data = await res.json();
        if (data.ok) {
          fetchTools();
        }
      } finally {
        setPromoting(null);
      }
    },
    [fetchTools]
  );

  const filtered = tools.filter((t) => {
    const matchesText =
      !filter ||
      t.name.toLowerCase().includes(filter.toLowerCase()) ||
      t.id.toLowerCase().includes(filter.toLowerCase()) ||
      t.tags.some((tag) => tag.toLowerCase().includes(filter.toLowerCase()));
    const matchesChannel = channelFilter === "all" || t.channel === channelFilter;
    return matchesText && matchesChannel;
  });

  const channelCounts = tools.reduce(
    (acc, t) => {
      acc[t.channel] = (acc[t.channel] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <div style={{ padding: "24px", maxWidth: 960, color: "#f8f8f2" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Registry Admin</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#a8b2b8" }}>
            {tools.length} packages across {Object.keys(channelCounts).length} channels
          </p>
        </div>
        <button
          onClick={fetchTools}
          style={{
            padding: "6px 14px",
            background: "rgba(80,250,123,.12)",
            color: "#50fa7b",
            border: "1px solid rgba(80,250,123,.2)",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <input
          type="text"
          placeholder="Filter packages..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            flex: 1,
            padding: "8px 12px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 6,
            color: "#f8f8f2",
            fontSize: 13,
          }}
        />
        <div style={{ display: "flex", gap: 4 }}>
          {["all", ...CHANNELS].map((ch) => (
            <button
              key={ch}
              onClick={() => setChannelFilter(ch)}
              style={{
                padding: "6px 12px",
                background: channelFilter === ch ? "rgba(80,250,123,.15)" : "#1e1e2e",
                color: channelFilter === ch ? "#50fa7b" : "#a8b2b8",
                border: `1px solid ${channelFilter === ch ? "rgba(80,250,123,.3)" : "rgba(255,255,255,0.06)"}`,
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 12,
                textTransform: "capitalize",
              }}
            >
              {ch}
              {ch !== "all" && channelCounts[ch] ? ` (${channelCounts[ch]})` : ch === "all" ? ` (${tools.length})` : ""}
            </button>
          ))}
        </div>
      </div>

      {/* Package list */}
      {loading ? (
        <p style={{ color: "#6272a4" }}>Loading registry...</p>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#6272a4" }}>
          {tools.length === 0 ? (
            <>
              <p>No registry index found.</p>
              <p style={{ fontSize: 13 }}>
                Run <code style={{ color: "#50fa7b" }}>make registry-build</code> to generate it.
              </p>
            </>
          ) : (
            <p>No packages match your filter.</p>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map((tool) => (
            <div
              key={tool.id}
              style={{
                background: "#1e1e2e",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 8,
                padding: "14px 18px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{tool.name}</span>
                    <span
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        borderRadius: 10,
                        background: `${CHANNEL_COLORS[tool.channel] || "#6272a4"}22`,
                        color: CHANNEL_COLORS[tool.channel] || "#6272a4",
                        border: `1px solid ${CHANNEL_COLORS[tool.channel] || "#6272a4"}44`,
                      }}
                    >
                      {tool.channel}
                    </span>
                    <span style={{ fontSize: 11, color: "#6272a4" }}>{tool.kind}</span>
                    <span style={{ fontSize: 11, color: "#6272a4" }}>v{tool.version}</span>
                  </div>
                  <p style={{ margin: "4px 0", fontSize: 13, color: "#a8b2b8", lineHeight: 1.4 }}>
                    {tool.summary || "No description"}
                  </p>
                  <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                    {tool.tags.map((tag) => (
                      <span
                        key={tag}
                        style={{
                          fontSize: 11,
                          padding: "1px 6px",
                          borderRadius: 4,
                          background: "#282a36",
                          color: "#6272a4",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                    {tool.resource_count > 0 && (
                      <span style={{ fontSize: 11, color: "#6272a4" }}>{tool.resource_count} resources</span>
                    )}
                  </div>
                </div>

                {/* Channel promotion buttons */}
                <div style={{ display: "flex", gap: 4, marginLeft: 16 }}>
                  {CHANNELS.filter((ch) => ch !== tool.channel).map((ch) => (
                    <button
                      key={ch}
                      onClick={() => promote(tool.id, ch)}
                      disabled={promoting === tool.id}
                      style={{
                        padding: "4px 10px",
                        fontSize: 11,
                        background: `${CHANNEL_COLORS[ch]}15`,
                        color: CHANNEL_COLORS[ch],
                        border: `1px solid ${CHANNEL_COLORS[ch]}33`,
                        borderRadius: 4,
                        cursor: promoting === tool.id ? "wait" : "pointer",
                        opacity: promoting === tool.id ? 0.5 : 1,
                      }}
                    >
                      {ch === "production" ? "Promote to prod" : ch === "staging" ? "Stage" : "Demote to dev"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
